import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { onCookieConsentChange, readCookieConsent, type CookiePreferences } from '@/lib/cookieConsent';
import { updateGoogleConsent } from '@/lib/consentMode';
import {
  loadGoogleAnalytics,
  pushAnalyticsPageView,
  shouldLoadGoogleAnalytics,
} from '@/lib/analyticsLoader';

/**
 * GTM/GA4, çerez banner'ında analitik veya pazarlama onayı verildikten sonra yüklenir.
 * index.html'deki Consent Mode varsayılanları rıza öncesi tüm depolamayı reddeder.
 */
const Analytics = () => {
  const location = useLocation();
  const initialSkip = useRef(true);

  useEffect(() => {
    const applyConsent = (preferences: CookiePreferences) => {
      updateGoogleConsent(preferences);
      if (shouldLoadGoogleAnalytics(preferences)) {
        loadGoogleAnalytics();
      }
    };

    const stored = readCookieConsent();
    if (stored) {
      applyConsent(stored.preferences);
    }

    return onCookieConsentChange((record) => {
      applyConsent(record.preferences);
    });
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;

    if (initialSkip.current) {
      initialSkip.current = false;
      return;
    }

    // Helmet yeni <title>'ı bu effect'ten sonra yazıyor, GTM ise etiketi ateşlerken
    // document.title'ı okuyor. Hemen gönderirsek görüntüleme bir önceki sayfanın
    // başlığıyla kaydedilir; bu yüzden başlık güncellenene kadar bekliyoruz.
    const previousTitle = document.title;
    let sent = false;
    let fallbackTimer = 0;
    let observer: MutationObserver | null = null;

    const send = () => {
      if (sent) return;
      sent = true;
      observer?.disconnect();
      window.clearTimeout(fallbackTimer);
      pushAnalyticsPageView(path);
    };

    observer = new MutationObserver(() => {
      if (document.title !== previousTitle) send();
    });
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });

    // Başlığın hiç değişmediği sayfalarda görüntülemenin kaybolmaması için.
    fallbackTimer = window.setTimeout(send, 1200);

    return send;
  }, [location.pathname, location.search]);

  return null;
};

export default Analytics;
